import { buildApiUrl, ADMIN_ENDPOINTS } from '../../configs/api.js'

export default {
  url(payload = {}) {
    const queryParams = {
      limit: 2000,
      ...(payload.type && { type: payload.type }),
      ...(payload.userId && { userId: payload.userId }),
      ...(payload.kycStatus && { status: payload.kycStatus }),
      ...(payload.sortKey && { sortKey: payload.sortKey }),
      ...(payload.sortOrder && { sortOrder: payload.sortOrder })
    }

    return buildApiUrl(ADMIN_ENDPOINTS.KYC_LIST, {
      queryParams
    })
  },

  parse(raw = {}, payload = {}) {
    // MonRapidoo API retourne statusCode/message au lieu de success
    if (!raw?.statusCode || raw.statusCode !== 200) {
      return {
        statusCode: raw?.statusCode || 500,
        message: raw?.message || 'Failed to fetch KYC data',
        data: null,
      }
    }

    const kycDocuments = raw.data?.data || [] // Tableau dans data.data
    const total = raw.data?.total || kycDocuments.length

    // 1. Mapping des données
    const formatted = kycDocuments.map(el => {
      const kycData = el.data || {}
      const metadata = el.metadata || {}
      const verification = el.verification || {}

      // Resolve the best name possible based on user type
      let resolvedName = 'Utilisateur'
      const personalName = kycData.personalInfo?.firstName || kycData.personalInfo?.lastName
        ? `${kycData.personalInfo.firstName || ''} ${kycData.personalInfo.lastName || ''}`.trim()
        : kycData.personalInfo?.fullName

      const businessName = kycData.personalInfo?.companyName || kycData.storeName || kycData.companyName

      if (el.userType === 'restaurant' || el.userType === 'merchant') {
        resolvedName = businessName || personalName || 'Entreprise'
      } else {
        resolvedName = personalName || businessName || kycData.name || 'Utilisateur'
      }

      return {
        id: String(el.id || ''),
        userId: String(el.userId || ''),
        type: String(el.userType || 'rider'),
        kycStatus: String(el.status || 'draft'),
        uploadedAt: el.submittedAt || el.createdAt,
        createdAt: el.createdAt,
        updatedAt: el.updatedAt,

        // Common fields used by Kyc.vue table and cards
        userName: String(resolvedName),
        userEmail: String(kycData.personalInfo?.email || kycData.userEmail || kycData.email || '-'),
        phoneNumber: String(kycData.personalInfo?.phone || kycData.phoneNumber || '-'),

        // Detailed fields for KycDocumentDetails
        birthDate: kycData.personalInfo?.birthDate || kycData.personalInfo?.dateOfBirth ? new Date(kycData.personalInfo.birthDate || kycData.personalInfo.dateOfBirth).toLocaleDateString() : '-',
        address: kycData.personalInfo?.address || '-',
        city: kycData.personalInfo?.city || '-',
        country: kycData.personalInfo?.country || kycData.personalInfo?.countryCode || '-',

        // ID document fields
        idType: String(kycData.identityDocument?.type || '-'),
        idNumber: String(kycData.identityDocument?.number || '-'),
        idExpiryDate: kycData.identityDocument?.expirationDate || '-',
        idFrontUrl: kycData.identityDocument?.frontPhotoUrl || '',
        idBackUrl: kycData.identityDocument?.backPhotoUrl || '',

        // Vehicle document fields
        vehicleType: String(kycData.vehicleDocument?.type || '-'),
        licenseNumber: String(kycData.vehicleDocument?.number || '-'),
        vehiclePhotoUrl: kycData.vehicleDocument?.photoUrl || '',

        // Selfie
        selfieUrl: kycData.selfieUrl || '',

        // Restaurant specific fields
        storeName: String(kycData.storeName || kycData.personalInfo?.companyName || ''),
        companyName: String(kycData.personalInfo?.companyName || ''),
        website: kycData.website || kycData.personalInfo?.website || '',
        postalCode: kycData.personalInfo?.postalCode || '',
        state: kycData.personalInfo?.state || '',

        // Metadata and verification
        requiredSteps: metadata.requiredSteps || [],
        completedSteps: metadata.completedSteps || [],
        nextAction: metadata.nextAction || '',
        rejectionReason: verification.reason || '',
        rejectionNotes: verification.notes || '',
        reviewBy: verification.reviewedBy || '',
        reviewedAt: verification.reviewedAt || '',

        // Raw kycData for the details view
        kycData: kycData,
      }
    })

    // 2. Application des filtres locaux (search, type, kycStatus)
    const searchTerm = (payload?.search || '').toString().trim().toLowerCase()
    const filterType = (payload?.type || '').toString().trim().toLowerCase()
    const filterStatus = (payload?.kycStatus || payload?.status || '').toString().trim().toLowerCase()

    const matchesType = (itemType, filter) => {
      if (!filter) return true
      const norm = (itemType || '').toLowerCase()
      if (filter === 'restaurant') return norm === 'restaurant' || norm === 'merchant'
      if (filter === 'rider') return norm === 'rider'
      return norm === filter
    }

    const matchesStatus = (itemStatus, filter) => {
      if (!filter) return true
      const norm = (itemStatus || '').toLowerCase()
      if (filter === 'approved' || filter === 'validated') {
        return norm === 'approved' || norm === 'validated' || norm === 'valide' || norm === 'validé'
      }
      if (filter === 'submitted' || filter === 'pending') {
        return norm === 'submitted' || norm === 'pending' || norm === 'filled' || norm === 'under_review' || norm === 'en attente'
      }
      if (filter === 'rejected') {
        return norm === 'rejected' || norm === 'rejete' || norm === 'rejeté'
      }
      return norm === filter
    }

    let filtered = formatted.filter(k => {
      const typeOk = matchesType(k.type, filterType)
      const statusOk = matchesStatus(k.kycStatus, filterStatus)
      if (!typeOk || !statusOk) return false

      if (!searchTerm) return true
      return (
        k.id.toLowerCase().includes(searchTerm) ||
        k.userId.toLowerCase().includes(searchTerm) ||
        k.userName.toLowerCase().includes(searchTerm) ||
        k.userEmail.toLowerCase().includes(searchTerm) ||
        k.phoneNumber.toLowerCase().includes(searchTerm) ||
        k.kycStatus.toLowerCase().includes(searchTerm) ||
        k.idNumber.toLowerCase().includes(searchTerm) ||
        k.licenseNumber.toLowerCase().includes(searchTerm) ||
        k.storeName.toLowerCase().includes(searchTerm) ||
        k.type.toLowerCase().includes(searchTerm)
      )
    })

    // === Pagination locale ===
    const { page = 0, limit = 20 } = payload || {}
    const paginated = filtered.slice(page * limit, (page + 1) * limit)

    // 3. Retour avec total dynamique
    return {
      statusCode: 200,
      message: 'Ok',
      data: {
        total: filtered.length,
        result: paginated,
      },
    }
  },
}